export type ScreenAudioProcessorOptions = {
  initialGain?: number;
};

export class ScreenAudioProcessor {
  private ctx: AudioContext;
  private source: MediaStreamAudioSourceNode;
  private analyser: AnalyserNode;
  private gainNode: GainNode;
  private compressor: DynamicsCompressorNode;
  private limiter: DynamicsCompressorNode;
  private destination: MediaStreamAudioDestinationNode;

  private dataArray: Float32Array;

  constructor(track: MediaStreamTrack, opts?: ScreenAudioProcessorOptions) {
    this.ctx = new AudioContext();

    const stream = new MediaStream([track]);
    this.source = this.ctx.createMediaStreamSource(stream);

    /** 📊 Analyser (RMS / Peak) */
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.dataArray = new Float32Array(this.analyser.fftSize);

    /** 🎛 Compressor (nhẹ – giống Zoom) */
    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -18;
    this.compressor.knee.value = 6;
    this.compressor.ratio.value = 3;
    this.compressor.attack.value = 0.003;
    this.compressor.release.value = 0.25;

    /** 🔊 Gain */
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = opts?.initialGain ?? 1.2;

    /** 🚧 Limiter (chống vỡ) */
    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -1;
    this.limiter.knee.value = 0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.001;
    this.limiter.release.value = 0.1;

    /** 🎯 Output */
    this.destination = this.ctx.createMediaStreamDestination();

    /** 🔗 Connect pipeline */
    this.source
      .connect(this.analyser)
      .connect(this.compressor)
      .connect(this.gainNode)
      .connect(this.limiter)
      .connect(this.destination);
  }

  /** 📈 RMS (0 → ~1) */
  getRMS(): number {
    this.analyser.getFloatTimeDomainData(this.dataArray);

    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += this.dataArray[i] ** 2;
    }

    return Math.sqrt(sum / this.dataArray.length);
  }

  /** 🔊 Peak */
  getPeak(): number {
    this.analyser.getFloatTimeDomainData(this.dataArray);
    return Math.max(...this.dataArray.map(Math.abs));
  }

  /** 🤖 Auto gain (optional) */
  autoAdjustGain() {
    const rms = this.getRMS();

    if (rms < 0.05) this.gainNode.gain.value = 1.8;
    else if (rms < 0.1) this.gainNode.gain.value = 1.4;
    else this.gainNode.gain.value = 1.0;
  }

  /** 🎧 Track đã xử lý */
  getProcessedTrack(): MediaStreamTrack {
    return this.destination.stream.getAudioTracks()[0];
  }

  stop() {
    this.ctx.close();
  }
}
